// src/lib/style3d.ts — F10-T1: extrudarea 3D dinamică din date 2D (REQ-M3D-001/007).
// Funcții PURE, zero dependințe de DOM/DB — folosite de map.client (randare) și de teste.
// Config-ul per layer trăiește în `versions.config.style3d` (whitelist PATCH în layers.ts,
// propagat prin LayerRef din get_map — deci ajunge identic și în harta publică /embed).

import { attr_text, find_by_utr_code, regim_to_meters, UTR_COD_KEYS } from "./rlu"

/** Config-ul 3D al unui layer (`versions.config.style3d`). Toate câmpurile opționale —
 *  absent/gol = comportamentul implicit (heuristica pe slug + lanțul generic de chei). */
export interface Style3d {
	/** Stratul se extrudează în modul 3D. Absent → heuristica istorică pe slug (clădiri);
	 *  `false` EXPLICIT = exclus chiar dacă heuristica l-ar prinde. */
	enabled?: boolean
	height_attr?: string // atribut numeric = metri
	levels_attr?: string // atribut numeric = etaje → ×3m
	regim_attr?: string // atribut text regim RO („P+2") → parser
	/** Înălțimea din `rh_max` al UTR-ului care CONȚINE feature-ul (join dinamic în client:
	 *  centroid → poligon UTR → indicatori din metadata.rlu). Cere hartă cu strat UTR + RLU. */
	regim_utr?: boolean
	base_attr?: string // atribut numeric = metri la care începe extrudarea (implicit 0)
	color?: string // culoarea extrudării (implicit culoarea stratului)
	opacity?: number // 0..1 (implicit 0.85)
}

/** Etaj standard (plan/F10: „etaje×3m"). */
export const FLOOR_M = 3
/** Înălțimea când nu există nicio sursă (parter generos). */
export const DEFAULT_HEIGHT_M = 4.5
/** Plafon de siguranță — un atribut corupt (ex. cotă absolută) nu aruncă camera în stratosferă. */
export const MAX_HEIGHT_M = 60

// Lanțul GENERIC de chei (fallback când style3d nu numește atributul) — moștenit din prototip.
const HEIGHT_KEYS = ["inaltime", "inaltime_m", "h_max", "height", "h"]
const LEVELS_KEYS = ["nivele", "niveluri", "nr_niveluri", "etaje", "levels"]
const REGIM_KEYS = ["regim", "regim_inaltime", "regim_de_inaltime", "rh", "regim_h"]

/** Coerciție defensivă la Style3d — config-ul vine prin PATCH-ul generic (JSON liber). */
export function parse_style3d(v: unknown): Style3d {
	if (v === null || typeof v !== "object" || Array.isArray(v)) {
		return {}
	}
	const o = v as Record<string, unknown>
	const s = (k: string) => (typeof o[k] === "string" ? (o[k] as string).trim() : "")
	const out: Style3d = {}
	if (typeof o.enabled === "boolean") {
		out.enabled = o.enabled
	}
	for (const k of ["height_attr", "levels_attr", "regim_attr", "base_attr", "color"] as const) {
		const val = s(k)
		if (val) {
			out[k] = val
		}
	}
	if (o.regim_utr === true) {
		out.regim_utr = true
	}
	const op = Number(o.opacity)
	if (Number.isFinite(op) && op > 0 && op <= 1) {
		out.opacity = op
	}
	return out
}

function num_attr(props: Record<string, unknown>, key: string): number {
	const v = Number(attr_text(props, [key.toLowerCase()]))
	return Number.isFinite(v) ? v : 0
}

/**
 * Înălțimea unui feature din atributele lui, în metri (0 = nimic utilizabil).
 * Precedența: atributele NUMITE în style3d (height → levels → regim), apoi lanțul generic
 * de chei. Join-ul UTR (`regim_utr`) NU e aici — cere geometrie + metadata, vezi height_from_utr.
 */
export function feature_height_m(props: Record<string, unknown>, s3d: Style3d): number {
	if (s3d.height_attr) {
		const v = num_attr(props, s3d.height_attr)
		if (v > 0) {
			return Math.min(v, MAX_HEIGHT_M)
		}
	}
	if (s3d.levels_attr) {
		const v = num_attr(props, s3d.levels_attr)
		if (v > 0) {
			return Math.min(v * FLOOR_M, MAX_HEIGHT_M)
		}
	}
	if (s3d.regim_attr) {
		const v = regim_to_meters(attr_text(props, [s3d.regim_attr.toLowerCase()]))
		if (v > 0) {
			return v
		}
	}
	for (const k of HEIGHT_KEYS) {
		const v = num_attr(props, k)
		if (v > 0) {
			return Math.min(v, MAX_HEIGHT_M)
		}
	}
	for (const k of LEVELS_KEYS) {
		const v = num_attr(props, k)
		if (v > 0) {
			return Math.min(v * FLOOR_M, MAX_HEIGHT_M)
		}
	}
	for (const k of REGIM_KEYS) {
		const v = regim_to_meters(attr_text(props, [k]))
		if (v > 0) {
			return v
		}
	}
	return 0
}

/** Fallback determinist per feature (hash stabil pe id → 3.5–7.5m) — satul arată organic. */
export function pseudo_height_m(id: string): number {
	let h = 0
	for (let i = 0; i < id.length; i++) {
		h = (h * 31 + id.charCodeAt(i)) | 0
	}
	return 3.5 + (Math.abs(h) % 9) * 0.5
}

// ─────────────────────────────────────────────────────────────
// Join-ul dinamic feature → UTR (REQ-M3D-001: „generare DINAMICĂ din date 2D")
// Containere PURE pe lng/lat: conținerea e topologică, nu metrică — ray casting e corect
// pe coordonate geografice la scara unui UTR (NU măsura arii aici, vezi lib/geodesy).
// ─────────────────────────────────────────────────────────────

type Ring = number[][] // [[lng, lat, (z?)], …] — indexăm doar [0]/[1], robust la XYZ (F10-T2)

function point_in_ring(lng: number, lat: number, ring: Ring): boolean {
	let inside = false
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const [xi, yi] = ring[i]
		const [xj, yj] = ring[j]
		if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
			inside = !inside
		}
	}
	return inside
}

/** Paritate pe toate inelele (exterior + găuri) ale unui set de poligoane. */
function point_in_rings(lng: number, lat: number, polys: Ring[][]): boolean {
	let crossings = 0
	for (const rings of polys) {
		for (const ring of rings) {
			if (point_in_ring(lng, lat, ring)) {
				crossings++
			}
		}
	}
	return crossings % 2 === 1
}

/** Punctul e în geometria (Multi)Polygon dată ca `{type, coordinates}` GeoJSON? Alte tipuri → false. */
export function point_in_geometry(
	lng: number,
	lat: number,
	geometry: { type?: string; coordinates?: unknown } | null | undefined,
): boolean {
	if (!geometry || !Array.isArray(geometry.coordinates)) {
		return false
	}
	if (geometry.type === "Polygon") {
		return point_in_rings(lng, lat, [geometry.coordinates as Ring[]])
	}
	if (geometry.type === "MultiPolygon") {
		return point_in_rings(lng, lat, geometry.coordinates as Ring[][])
	}
	return false
}

/**
 * Punct reprezentativ al unei geometrii (Multi)Polygon: centroidul (shoelace) primului inel
 * exterior — suficient ca ancoră de join pentru clădiri (concavități extreme pot ancora în
 * UTR-ul vecin: estimare asumată, nu act juridic). [0,0] = geometrie inutilizabilă.
 */
export function representative_point(
	geometry: { type?: string; coordinates?: unknown } | null | undefined,
): [number, number] {
	if (!geometry || !Array.isArray(geometry.coordinates)) {
		return [0, 0]
	}
	const outer: Ring | undefined =
		geometry.type === "Polygon"
			? (geometry.coordinates as Ring[])[0]
			: geometry.type === "MultiPolygon"
				? (geometry.coordinates as Ring[][])[0]?.[0]
				: undefined
	if (!outer || outer.length < 3) {
		return [0, 0]
	}
	// Centroid shoelace pe inelul exterior (închis sau nu — ultimul segment se închide implicit).
	let a = 0
	let cx = 0
	let cy = 0
	for (let i = 0, j = outer.length - 1; i < outer.length; j = i++) {
		const cross = outer[j][0] * outer[i][1] - outer[i][0] * outer[j][1]
		a += cross
		cx += (outer[j][0] + outer[i][0]) * cross
		cy += (outer[j][1] + outer[i][1]) * cross
	}
	if (a === 0) {
		// Degenerat (arie zero) → media vârfurilor.
		let sx = 0
		let sy = 0
		for (const p of outer) {
			sx += p[0]
			sy += p[1]
		}
		return [sx / outer.length, sy / outer.length]
	}
	return [cx / (3 * a), cy / (3 * a)]
}

// ─────────────────────────────────────────────────────────────
// Lookup-ul cod UTR → regim de înălțime (din metadata.rlu a hărții)
// ─────────────────────────────────────────────────────────────

/** Rândurile `utr_indicatori` din metadata hărții — defensiv (jsonb poate sosi ca string). */
export function rh_rows_from_metadata(metadata: unknown): Record<string, unknown>[] {
	let m = metadata
	if (typeof m === "string") {
		try {
			m = JSON.parse(m)
		} catch {
			return []
		}
	}
	if (m === null || typeof m !== "object") {
		return []
	}
	const rlu = (m as Record<string, unknown>).rlu
	if (rlu === null || typeof rlu !== "object" || Array.isArray(rlu)) {
		return []
	}
	const list = (rlu as Record<string, unknown>).utr_indicatori
	if (!Array.isArray(list)) {
		return []
	}
	return list.filter(
		(r): r is Record<string, unknown> => r !== null && typeof r === "object" && !Array.isArray(r),
	)
}

/** `rh_max` al codului UTR (cu fallback-ul de subzonă In1→In), ca text; "" = negăsit. */
export function rh_max_for(rows: Record<string, unknown>[], cod: string): string {
	const row = find_by_utr_code(rows, cod)
	const v = row.rh_max
	return v == null ? "" : String(v).trim()
}

/** Codul UTR al unui poligon UTR, din atributele lui (aceeași derivare ca serverul). */
export function utr_code_of(props: Record<string, unknown>): string {
	return attr_text(props, UTR_COD_KEYS)
}

/** Intrare pregătită pentru join-ul spațial: bbox-ul (pre-filtru ieftin) + geometria + codul. */
export interface UtrIndexEntry {
	bbox: [number, number, number, number] // [minlng, minlat, maxlng, maxlat]
	geometry: { type?: string; coordinates?: unknown }
	cod: string
}

function geometry_bbox(geometry: {
	type?: string
	coordinates?: unknown
}): [number, number, number, number] {
	let minx = Infinity
	let miny = Infinity
	let maxx = -Infinity
	let maxy = -Infinity
	const walk = (arr: unknown): void => {
		if (!Array.isArray(arr)) {
			return
		}
		if (typeof arr[0] === "number" && typeof arr[1] === "number") {
			const x = arr[0] as number
			const y = arr[1] as number
			minx = Math.min(minx, x)
			miny = Math.min(miny, y)
			maxx = Math.max(maxx, x)
			maxy = Math.max(maxy, y)
			return
		}
		for (const a of arr) {
			walk(a)
		}
	}
	walk(geometry.coordinates)
	return [minx, miny, maxx, maxy]
}

/**
 * Pregătește poligoanele UTR pentru join-ul per feature: doar (Multi)Polygon cu cod UTR
 * derivabil (aceleași chei ca serverul); bbox-ul taie candidații înainte de ray casting —
 * pe date reale (mii de clădiri × sute de UTR-uri CAD stufoase) diferența e ordine de mărime.
 */
export function prepare_utr_index(
	utr_features: {
		geometry?: { type?: string; coordinates?: unknown } | null
		properties?: Record<string, unknown> | null
	}[],
): UtrIndexEntry[] {
	const out: UtrIndexEntry[] = []
	for (const u of utr_features) {
		const g = u.geometry
		if (!g || (g.type !== "Polygon" && g.type !== "MultiPolygon")) {
			continue
		}
		const cod = utr_code_of(u.properties ?? {})
		if (!cod) {
			continue
		}
		out.push({ bbox: geometry_bbox(g), geometry: g, cod })
	}
	return out
}

/** Varianta pe index a join-ului: punct reprezentativ → bbox → conținere → cod → rh_max → metri. */
export function height_from_utr_indexed(
	feature_geometry: { type?: string; coordinates?: unknown } | null | undefined,
	index: UtrIndexEntry[],
	rh_rows: Record<string, unknown>[],
): number {
	if (rh_rows.length === 0 || index.length === 0) {
		return 0
	}
	const [lng, lat] = representative_point(feature_geometry)
	if (lng === 0 && lat === 0) {
		return 0
	}
	for (const u of index) {
		if (lng < u.bbox[0] || lng > u.bbox[2] || lat < u.bbox[1] || lat > u.bbox[3]) {
			continue
		}
		if (point_in_geometry(lng, lat, u.geometry)) {
			return regim_to_meters(rh_max_for(rh_rows, u.cod))
		}
	}
	return 0
}

/**
 * Înălțimea în metri a unui feature prin join-ul dinamic cu stratul UTR: punct reprezentativ →
 * primul poligon UTR care-l conține → codul lui → rh_max din indicatori → parser. 0 = fără UTR /
 * fără rh interpretabil (apelantul cade pe lanțul următor de fallback).
 */
export function height_from_utr(
	feature_geometry: { type?: string; coordinates?: unknown } | null | undefined,
	utr_features: {
		geometry?: { type?: string; coordinates?: unknown } | null
		properties?: Record<string, unknown> | null
	}[],
	rh_rows: Record<string, unknown>[],
): number {
	return height_from_utr_indexed(feature_geometry, prepare_utr_index(utr_features), rh_rows)
}

// ─────────────────────────────────────────────────────────────
// Înălțimile REZOLVATE ale unui strat întreg (fix-ul MVT, constatarea #4 din review-ul staging)
// ─────────────────────────────────────────────────────────────

/** Rezultatul agregat: feature_id → metri (+ bazele, doar cu `base_attr` configurat). */
export interface Heights3d {
	heights: Record<string, number>
	bases: Record<string, number>
}

/** Contextul join-ului UTR, în forma cerută de compute_heights3d (partajat de toți apelanții). */
export interface UtrCtx {
	index: UtrIndexEntry[]
	rows: Record<string, unknown>[]
}
export const NIL_HEIGHTS3D: Heights3d = Object.freeze({
	heights: Object.freeze({}),
	bases: Object.freeze({}),
})

/**
 * Calculează înălțimea FINALĂ pentru fiecare feature al unui strat — EXACT lanțul de precedență
 * pe care clientul îl aplică pe sursele GeoJSON (atribute numite → lanț generic → join UTR →
 * fallback pseudo-determinist). Folosit de SERVER pentru straturile servite ca MVT (tile-urile
 * nu transportă atribute) — clientul aplică rezultatul prin setFeatureState; funcția e PURĂ ca
 * GeoJSON-ul și MVT-ul să dea înălțimi IDENTICE (anti-drift).
 */
export function compute_heights3d(
	features: {
		id?: string | number | null
		geometry?: { type?: string; coordinates?: unknown } | null
		properties?: Record<string, unknown> | null
	}[],
	s3d: Style3d,
	utr: UtrCtx | null,
): Heights3d {
	const heights: Record<string, number> = {}
	const bases: Record<string, number> = {}
	for (const f of features) {
		const props = (f.properties ?? {}) as Record<string, unknown>
		const id = String(f.id ?? props.feature_id ?? "")
		if (!id) {
			continue // fără id nu există cheie de feature-state; apelanții cad pe DEFAULT_HEIGHT_M
		}
		let h = feature_height_m(props, s3d)
		if (h === 0 && s3d.regim_utr && utr) {
			h = height_from_utr_indexed(f.geometry, utr.index, utr.rows)
		}
		heights[id] = h || pseudo_height_m(id)
		if (s3d.base_attr) {
			const b = Number(props[s3d.base_attr])
			if (Number.isFinite(b) && b > 0) {
				bases[id] = b
			}
		}
	}
	return { heights, bases }
}
